import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { AdminTableResponse } from '@kome/shared';
import TopBar from '@/components/TopBar';
import Card from '@/components/Card';
import Badge from '@/components/Badge';
import Button from '@/components/Button';
import DataTable from '@/components/DataTable';
import type { Column } from '@/components/DataTable';
import { adminApi, AdminApiError } from '@/lib/api';

/**
 * DB 조회 화면 (읽기 전용 뷰어, SPEC §2.4 M-04).
 * - 화이트리스트 테이블만 드롭다운으로 선택 → api.dbTable(table, limit, offset)
 * - 응답의 columns/rows 를 DataTable 로 표시(서버 페이지네이션)
 * - 민감 컬럼(password_hash 등)은 서버가 '***' 로 마스킹해 내려줌
 */

/** 조회 가능한 테이블 (서버 admin.ts 의 ALLOWED_TABLES 와 정합) */
const TABLES: { table: string; label: string }[] = [
  { table: 'users', label: '사용자 (users)' },
  { table: 'rooms', label: '채팅방 (rooms)' },
  { table: 'messages', label: '메시지 (messages)' },
  { table: 'room_members', label: '방 멤버 (room_members)' },
  { table: 'friendships', label: '친구관계 (friendships)' },
  { table: 'integrations', label: '외부연동 (integrations)' },
  { table: 'daily_summaries', label: '일일요약 (daily_summaries)' },
  { table: 'admin_audit', label: '감사로그 (admin_audit)' },
  { table: 'calls', label: '통화기록 (calls)' },
];

/** 서버가 마스킹하는 민감 컬럼(표시용 배지 안내) — 서버 SENSITIVE_COLUMNS 와 정합 */
const SENSITIVE_COLUMNS = new Set([
  'password_hash',
  'password_salt',
  'access_token',
  'refresh_token',
]);

const PAGE_SIZE = 50;

/** 긴 셀 값 말줄임 스타일 */
const CELL_TRUNCATE_STYLE: CSSProperties = {
  display: 'inline-block',
  maxWidth: '320px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'bottom',
};

/** 셀 값 표시: null/긴 JSON/타임스탬프를 사람이 읽기 쉽게 정리 */
function renderCellValue(value: unknown, column: string): ReactNode {
  if (value == null) return <span className="muted">—</span>;

  // 서버가 마스킹한 값
  if (value === '***') {
    return <Badge tone="warning">마스킹됨</Badge>;
  }

  // 시각 컬럼(epoch ms) → 로컬 시각 + 원본 병기
  if (typeof value === 'number' && /_at$|^expires_at$/.test(column) && value > 1_000_000_000_000) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      return (
        <span title={String(value)}>{d.toLocaleString('ko-KR')}</span>
      );
    }
  }

  // is_active 같은 0/1 불리언성 정수
  if (typeof value === 'number' && /^is_|^deleted$|^favorite$|^hidden$|^muted$|^pinned$|^delivered$|^uploaded$/.test(column)) {
    return value ? (
      <Badge tone="success">true</Badge>
    ) : (
      <Badge tone="neutral">false</Badge>
    );
  }

  if (typeof value === 'boolean') {
    return value ? <Badge tone="success">true</Badge> : <Badge tone="neutral">false</Badge>;
  }

  if (typeof value === 'object') {
    const json = JSON.stringify(value);
    return (
      <span style={CELL_TRUNCATE_STYLE} title={json}>
        {json}
      </span>
    );
  }

  const str = String(value);
  if (str.length > 120) {
    return (
      <span style={CELL_TRUNCATE_STYLE} title={str}>
        {str.slice(0, 120)}…
      </span>
    );
  }
  return str;
}

export default function Database() {
  const [table, setTable] = useState<string>(TABLES[0].table);
  const [page, setPage] = useState(0); // 0-based
  const [data, setData] = useState<AdminTableResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 마지막 요청만 반영(테이블/페이지 빠르게 변경 시 경쟁 방지)
  const reqIdRef = useRef(0);

  const load = useCallback(
    async (targetTable: string, targetPage: number) => {
      const reqId = ++reqIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const offset = targetPage * PAGE_SIZE;
        const res = await adminApi.dbTable(targetTable, PAGE_SIZE, offset);
        // 서버가 { ok, data } 래퍼로 응답하는 경우까지 방어적으로 언랩
        const normalized = unwrap(res);
        if (reqId !== reqIdRef.current) return; // 더 최신 요청이 있으면 폐기
        setData(normalized);
      } catch (e) {
        if (reqId !== reqIdRef.current) return;
        setData(null);
        setError(
          e instanceof AdminApiError ? e.message : '테이블을 불러오지 못했습니다.',
        );
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void load(table, page);
  }, [table, page, load]);

  const handleTableChange = (next: string) => {
    if (next === table) return;
    setData(null);
    setPage(0);
    setTable(next);
  };

  const columns = data?.columns ?? [];
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  // 민감 컬럼이 현재 테이블에 존재하는지(안내 배지 노출용)
  const maskedColumns = useMemo(
    () => columns.filter((c) => SENSITIVE_COLUMNS.has(c)),
    [columns],
  );

  // 동적 컬럼 정의: render 로 값 포맷, 정렬은 클라이언트(현재 페이지 내)에서 처리
  const tableColumns: Column<Record<string, unknown>>[] = useMemo(
    () =>
      columns.map((col) => ({
        key: col,
        header: col,
        render: (row) => renderCellValue(row[col], col),
      })),
    [columns],
  );

  // 행 키: id 컬럼 우선, 없으면 인덱스
  const rowKey = useCallback(
    (row: Record<string, unknown>, index: number) =>
      row.id != null ? String(row.id) : `${table}-${page}-${index}`,
    [table, page],
  );

  const currentLabel =
    TABLES.find((t) => t.table === table)?.label ?? table;

  return (
    <>
      <TopBar
        title="DB"
        subtitle="테이블 조회 (읽기 전용)"
        actions={
          <Button
            variant="secondary"
            size="sm"
            loading={loading}
            onClick={() => void load(table, page)}
          >
            새로고침
          </Button>
        }
      />
      <div className="admin-content">
        <Card
          title={currentLabel}
          actions={
            <div className="row" style={{ gap: 'var(--space-md)' }}>
              {maskedColumns.length > 0 && (
                <Badge tone="warning">민감 컬럼 {maskedColumns.length}개 마스킹됨</Badge>
              )}
              <label className="row">
                <span className="muted" style={{ fontSize: 'var(--font-size-sm)' }}>
                  테이블
                </span>
                <select
                  className="input"
                  style={{ width: 'auto', minWidth: '180px' }}
                  value={table}
                  onChange={(e) => handleTableChange(e.target.value)}
                  disabled={loading}
                >
                  {TABLES.map((t) => (
                    <option key={t.table} value={t.table}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          }
          noPadding
        >
          {error ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 'var(--space-md)',
                padding: 'var(--space-xxl)',
              }}
            >
              <div className="muted">{error}</div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void load(table, page)}
              >
                다시 시도
              </Button>
            </div>
          ) : (
            <DataTable<Record<string, unknown>>
              columns={tableColumns}
              rows={rows}
              rowKey={rowKey}
              loading={loading}
              emptyText="이 테이블에 데이터가 없습니다."
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          )}
        </Card>
      </div>
    </>
  );
}

/** { ok, data } 래퍼 응답을 방어적으로 언랩 (API 클라이언트가 이미 언랩했다면 그대로) */
function unwrap(res: AdminTableResponse | { ok: true; data: AdminTableResponse }): AdminTableResponse {
  if (
    res &&
    typeof res === 'object' &&
    'data' in res &&
    !('columns' in res) &&
    (res as { data?: unknown }).data &&
    typeof (res as { data?: unknown }).data === 'object'
  ) {
    return (res as { data: AdminTableResponse }).data;
  }
  return res as AdminTableResponse;
}
